import { useEffect, useMemo, useState } from "react";
import api, { extractErr, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Upload, ImageIcon } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useTranslation } from "react-i18next";
import { useFileUpload } from "@/lib/uploadProgress";
import AdminProductTags from "./AdminProductTags";

const EMPTY = { name: "", slug: "", image_url: "", sort_order: 0, parent_id: "" };

function parentOf(items, c) {
  return items.find((x) => x.id === c.parent_id) || null;
}

export default function AdminCategories() {
  const { t } = useTranslation();
  const uploadFile = useFileUpload();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const load = () => api.get("/merchant/categories").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const ordered = useMemo(() => {
    const roots = items.filter((c) => !c.parent_id).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    const out = [];
    roots.forEach((r) => {
      out.push({ ...r, _depth: 0 });
      items
        .filter((c) => c.parent_id === r.id)
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
        .forEach((ch) => out.push({ ...ch, _depth: 1 }));
    });
    items.filter((c) => c.parent_id && !items.some((p) => p.id === c.parent_id)).forEach((orphan) => {
      if (!out.some((x) => x.id === orphan.id)) out.push({ ...orphan, _depth: 0 });
    });
    return out;
  }, [items]);

  const roots = items.filter((c) => !c.parent_id);
  const parentChoices = editing?.id
    ? roots.filter((c) => c.id !== editing.id)
    : roots;

  const openNew = (parentId = "") => { setEditing({ ...EMPTY, parent_id: parentId || "" }); setOpen(true); };
  const openEdit = (c) => { setEditing({ ...EMPTY, ...c, parent_id: c.parent_id || "" }); setOpen(true); };

  const uploadImage = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("kind", "category");
    setUploading(true);
    try {
      const data = await uploadFile("/merchant/media/upload", fd);
      setEditing((e) => ({ ...e, image_url: data.url }));
    } catch (e) { toast.error(extractErr(e)); } finally { setUploading(false); }
  };

  const save = async () => {
    try {
      const payload = {
        name: editing.name,
        slug: editing.slug || undefined,
        image_url: editing.image_url || null,
        sort_order: parseInt(editing.sort_order || 0, 10),
        parent_id: editing.parent_id || null,
      };
      if (editing.id) await api.put(`/merchant/categories/${editing.id}`, payload);
      else await api.post("/merchant/categories", payload);
      toast.success(t("common.saved"));
      setOpen(false);
      load();
    } catch (e) { toast.error(extractErr(e)); }
  };

  const del = async (c) => {
    try { await api.delete(`/merchant/categories/${c.id}`); toast.success(t("common.delete")); setSelected((s) => { const n = new Set(s); n.delete(c.id); return n; }); load(); } catch (e) { toast.error(extractErr(e)); }
  };

  const allSelected = ordered.length > 0 && ordered.every((c) => selected.has(c.id));
  const someSelected = ordered.some((c) => selected.has(c.id));
  const toggleOne = (id) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleAll = () => setSelected((s) => {
    const n = new Set(s);
    if (allSelected) ordered.forEach((c) => n.delete(c.id));
    else ordered.forEach((c) => n.add(c.id));
    return n;
  });
  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    setBulkDeleting(true);
    try {
      const { data } = await api.post("/merchant/categories/bulk-delete", { ids });
      toast.success(`Deleted ${data.deleted} categor${data.deleted === 1 ? "y" : "ies"}`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(extractErr(e)); } finally { setBulkDeleting(false); }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <Tabs defaultValue="categories">
        <TabsList data-testid="catalog-tabs">
          <TabsTrigger value="categories" data-testid="tab-categories">Categories</TabsTrigger>
          <TabsTrigger value="tags" data-testid="tab-tags">Tags</TabsTrigger>
        </TabsList>
        <TabsContent value="categories" className="space-y-6 mt-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">{t("categories.title")}</h1>
          <p className="text-slate-500 text-sm mt-1">{t("categories.count", { count: items.length })} · Subcategories nest under a parent</p>
        </div>
        <Button onClick={() => openNew("")} data-testid="categories-new-btn" className="rounded-full bg-blue-600 hover:bg-blue-700"><Plus className="w-4 h-4 mr-1" /> {t("categories.new")}</Button>
      </div>

      {selected.size > 0 && (
        <div className="flex items-center justify-between gap-3 flex-wrap rounded-xl border border-blue-200 bg-blue-50/70 px-4 py-3" data-testid="categories-bulk-bar">
          <div className="text-sm font-medium text-slate-700"><span data-testid="categories-selected-count">{selected.size}</span> selected</div>
          <div className="flex gap-2">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" className="rounded-full bg-white text-red-600 hover:text-red-700 border-red-200" data-testid="categories-bulk-delete-btn"><Trash2 className="w-4 h-4 mr-1" /> Delete selected</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete {selected.size} categor{selected.size === 1 ? "y" : "ies"}?</AlertDialogTitle>
                  <AlertDialogDescription>Products keep their other categories. Subcategories of deleted parents become top-level.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                  <AlertDialogAction onClick={bulkDelete} disabled={bulkDeleting} className="bg-red-600 hover:bg-red-700" data-testid="categories-bulk-delete-confirm">{bulkDeleting ? "Deleting…" : t("common.delete")}</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <Button variant="ghost" size="sm" className="rounded-full" onClick={() => setSelected(new Set())} data-testid="categories-clear-selection">Clear</Button>
          </div>
        </div>
      )}

      <Card className="p-0 overflow-hidden border-slate-200/70">
        {items.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">{t("categories.empty")}</div>
        ) : (
          <div className="divide-y divide-slate-100">
            <div className="px-4 py-2.5 flex items-center gap-4 bg-slate-50/70 text-xs font-medium text-slate-500">
              <Checkbox checked={allSelected ? true : (someSelected ? "indeterminate" : false)} onCheckedChange={toggleAll} data-testid="categories-select-all" aria-label="Select all" />
              <span>Select all</span>
            </div>
            {ordered.map((c) => (
              <div key={c.id} className={`p-4 flex items-center gap-4 ${selected.has(c.id) ? "bg-blue-50/40" : ""}`} style={{ paddingLeft: c._depth ? 40 : 16 }} data-testid={`cat-row-${c.slug}`}>
                <Checkbox checked={selected.has(c.id)} onCheckedChange={() => toggleOne(c.id)} data-testid={`cat-select-${c.slug}`} aria-label={`Select ${c.name}`} />
                <div className="w-12 h-12 rounded-lg bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center flex-shrink-0">
                  {c.image_url ? <img src={mediaUrl(c.image_url)} alt={c.name} className="w-full h-full object-cover" /> : <ImageIcon className="w-5 h-5 text-slate-300" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900">{c.name}{c._depth ? <span className="ml-2 text-[11px] font-normal text-slate-400">subcategory</span> : null}</div>
                  <div className="text-xs font-mono-nums text-slate-500">/{c.slug} · order {c.sort_order}{c.parent_id && parentOf(items, c) ? ` · under ${parentOf(items, c).name}` : ""} · <span data-testid={`cat-count-${c.slug}`}>{c.product_count || 0} product{(c.product_count || 0) === 1 ? "" : "s"}</span></div>
                </div>
                {!c.parent_id && (
                  <Button variant="outline" size="sm" onClick={() => openNew(c.id)} data-testid={`cat-add-sub-${c.slug}`}>Add subcategory</Button>
                )}
                <Button variant="outline" size="sm" onClick={() => openEdit(c)} data-testid={`cat-edit-${c.slug}`}><Pencil className="w-4 h-4" /></Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild><Button variant="outline" size="sm" data-testid={`cat-delete-${c.slug}`}><Trash2 className="w-4 h-4 text-red-600" /></Button></AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader><AlertDialogTitle>Delete category?</AlertDialogTitle><AlertDialogDescription>Products in this category will lose their tag but not be deleted. Subcategories become top-level.</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter><AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel><AlertDialogAction onClick={() => del(c)} className="bg-red-600 hover:bg-red-700">{t("common.delete")}</AlertDialogAction></AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing?.id ? "Edit category" : (editing?.parent_id ? "New subcategory" : "New category")}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <Label>Name</Label>
                <Input data-testid="cat-form-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              </div>
              <div>
                <Label>Parent category</Label>
                <select
                  className="mt-1 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                  data-testid="cat-form-parent"
                  value={editing.parent_id || ""}
                  onChange={(e) => setEditing({ ...editing, parent_id: e.target.value })}
                >
                  <option value="">None (top-level)</option>
                  {parentChoices.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                <p className="text-[11px] text-slate-400 mt-1">One level of nesting. Subcategories cannot have children.</p>
              </div>
              <div>
                <Label>Slug (URL)</Label>
                <Input data-testid="cat-form-slug" value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} placeholder="auto" />
              </div>
              <div>
                <Label>Category image <span className="text-slate-400 text-xs font-normal">shown on homepage &amp; category page</span></Label>
                <div className="mt-1 flex items-center gap-3">
                  <div className="w-16 h-16 rounded-xl bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center flex-shrink-0">
                    {editing.image_url ? <img src={mediaUrl(editing.image_url)} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-6 h-6 text-slate-300" />}
                  </div>
                  <div>
                    <label className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 cursor-pointer" data-testid="cat-form-image">
                      <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : (editing.image_url ? "Replace image" : "Upload image")}
                      <input type="file" hidden accept="image/*" onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0])} />
                    </label>
                    <p className="text-[11px] text-slate-400 mt-1">Recommended 800×800 px (square). Scales on category tiles.</p>
                  </div>
                  {editing.image_url && <button type="button" className="text-xs text-red-600" onClick={() => setEditing({ ...editing, image_url: "" })} data-testid="cat-form-image-remove">Remove</button>}
                </div>
              </div>
              <div>
                <Label>Sort order</Label>
                <Input type="number" data-testid="cat-form-sort" value={editing.sort_order} onChange={(e) => setEditing({ ...editing, sort_order: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="cat-form-save" className="bg-blue-600 hover:bg-blue-700">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
        </TabsContent>
        <TabsContent value="tags" className="mt-6">
          <AdminProductTags />
        </TabsContent>
      </Tabs>
    </div>
  );
}
